<script setup lang="ts">
/**
 * 额度与消耗。
 *
 * 分页策略跟会话列表一样：游标放在 ref 里而不是响应式 state——
 * IntersectionObserver 会连续触发，只有同步的 ref 守卫才真的挡得住重复请求。
 *
 * `loadQuota()` 成功后除了本地 `quota.value`，还要调 `applyQuota(...)`：
 * 顶栏的额度条读的是 auth-store 里那一份，不写回去就会出现
 * "本页说剩 120、顶栏说剩 0" 的自相矛盾。
 *
 * ## 账本行的"会话归属"为什么要拿会话列表来判
 *
 * `LedgerEntry.conversationId` 只是一个**可空 id**：契约里既没有标题，也没有"是否已删除"，
 * 而服务端也给不出后者——会话是软删除，行还留在库里。于是本页额外拉一次会话列表当索引：
 *
 * - 命中 id → 显示会话**标题**并可点回那次对话（用户认的是标题，不是 cuid）
 * - 列表**完整**拉取过但没命中 → 会话确实已删除：只显示「会话已删除」，不给链接、不显示编号
 * - 列表没拉全或拉失败 → 只显示 `会话 <id前8位>` 且**不判定为已删除**
 *   （宁可少说一句，也不能把还在的会话标成"已删除"——那正是这次要修的 bug）
 * - `conversationId === null` → 「未关联会话」：null 的真实含义是"这次请求没带会话 id"
 *   （早期版本里会话的第一轮就是这样写的），**不等于会话被删了**
 */

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import {
  ConversationPageSchema,
  ConversationUsageResponseSchema,
  DEFAULT_PAGE_LIMIT,
  LedgerPageSchema,
  MAX_PAGE_LIMIT,
  QuotaResponseSchema,
  planLabelOf,
} from '@spirit/contracts';
import type {
  ConversationPage,
  ConversationUsageResponse,
  LedgerEntry,
  LedgerPage,
  Quota,
  QuotaResponse,
} from '@spirit/contracts';

import PlanBadge from '../components/quota/PlanBadge.vue';
import UsageMeter from '../components/quota/UsageMeter.vue';
import Button from '../components/ui/Button.vue';
import SkeletonText from '../components/ui/SkeletonText.vue';
import { apiRequest, isApiError } from '../lib/api-client';
import { applyQuota, useAuth } from '../lib/auth-store';
import { formatDateTime } from '../lib/format';
import {
  describeUsageEmpty,
  describeUsageItemCount,
  describeUsageLastAt,
  describeUsageRounds,
  describeUsageShare,
  describeUsageTotals,
  describeUsageTruncation,
  describeUsageWindow,
  describeLedgerKind,
  usageRowsOf,
} from '../lib/ledger-usage';
import { describeLedgerOwner, type LedgerOwnerView } from '../lib/ledger-owner';
import { quotaScopeTitleOf } from '../lib/quota-display';
import {
  describeBillingCycle,
  describeRemaining,
  EXPIRY_TICK_MS,
  expiryStatusOf,
  planBadgeToneOf,
} from '../lib/subscription';
import { loadSubscription, useSubscription } from '../lib/subscription-store';

const PAGE_SIZE = DEFAULT_PAGE_LIMIT;

/**
 * 会话索引最多翻几页（每页 `MAX_PAGE_LIMIT` 条）。
 * 只为一个展示字段服务，不值得无限翻页：翻不到就退化成"不判定为已删除"。
 */
const CONVERSATION_MAX_PAGES = 5;

const auth = useAuth();

const quota = ref<Quota | null>(auth.quota);
const entries = ref<LedgerEntry[]>([]);
const hasMore = ref(true);
const loadingQuota = ref(false);
const loadingLedger = ref(false);
const error = ref<string | null>(null);

// 三个分页守卫用 ref 而不是 state：见文件头注释。
let cursor: string | null = null;
let hasMoreGuard = true;
let loadingGuard = false;

async function loadQuota(): Promise<void> {
  loadingQuota.value = true;
  error.value = null;
  try {
    const result = await apiRequest<QuotaResponse>('/quota', QuotaResponseSchema, { method: 'GET' });
    quota.value = result.quota;
    // 让顶栏与这一页显示同一个数字。
    applyQuota(result.quota);
  } catch (caught) {
    error.value = isApiError(caught) ? caught.message : '额度信息加载失败，请稍后重试';
  } finally {
    loadingQuota.value = false;
  }
}

async function loadLedger(): Promise<void> {
  if (loadingGuard || !hasMoreGuard) return;
  loadingGuard = true;
  loadingLedger.value = true;
  error.value = null;
  try {
    const page = await apiRequest<LedgerPage>('/quota/ledger', LedgerPageSchema, {
      method: 'GET',
      query: cursor === null ? { limit: PAGE_SIZE } : { limit: PAGE_SIZE, cursor },
    });
    cursor = page.nextCursor;
    hasMoreGuard = page.nextCursor !== null;
    hasMore.value = hasMoreGuard;
    // 按 id 去重：重复触发（或重新挂载）不应该出现两行一样的记录。
    const seen = new Set(entries.value.map((item) => item.id));
    entries.value = [...entries.value, ...page.items.filter((item) => !seen.has(item.id))];
  } catch (caught) {
    error.value = isApiError(caught) ? caught.message : '消耗明细加载失败，请稍后重试';
  } finally {
    loadingGuard = false;
    loadingLedger.value = false;
  }
}

/** 会话 id → 标题。拉取失败时保留上一次的结果，而不是清空。 */
const conversationTitles = ref<Map<string, string>>(new Map());
/** 会话列表是否**完整**拉取过：只有完整时才有资格断言"这条记录指向的会话已经不存在"。 */
const conversationIndexComplete = ref(false);

/**
 * 拉会话列表当索引（见文件头"会话归属"一段）。
 *
 * 这里吞掉异常：额度与账本才是本页的主数据，索引只是用来判断归属的辅助信息，
 * 失败了把 `conversationIndexComplete` 置回 false 就够——页面会退化成"不判定已删除"。
 */
async function loadConversationIndex(): Promise<void> {
  const titles = new Map<string, string>();
  let nextCursor: string | null = null;
  try {
    for (let page = 0; page < CONVERSATION_MAX_PAGES; page += 1) {
      const result: ConversationPage = await apiRequest<ConversationPage>(
        '/conversations',
        ConversationPageSchema,
        {
          method: 'GET',
          query:
            nextCursor === null
              ? { limit: MAX_PAGE_LIMIT }
              : { limit: MAX_PAGE_LIMIT, cursor: nextCursor },
        },
      );
      for (const item of result.items) titles.set(item.id, item.title);
      nextCursor = result.nextCursor;
      if (nextCursor === null) break;
    }
  } catch {
    conversationIndexComplete.value = false;
    return;
  }
  conversationTitles.value = titles;
  conversationIndexComplete.value = nextCursor === null;
}

/** 明细行的展示形态：账本行 + 归属判定结果，模板里不再做任何判断。 */
interface LedgerRow {
  entry: LedgerEntry;
  owner: LedgerOwnerView;
}

const rows = computed<LedgerRow[]>(() =>
  entries.value.map((entry) => ({
    entry,
    owner: describeLedgerOwner(
      entry.conversationId,
      conversationTitles.value,
      conversationIndexComplete.value,
    ),
  })),
);

/**
 * 消耗明细的两种看法。
 *
 * 默认「按会话」：这是用户的自然口径（"我这个月在这个话题上花了多少"），
 * 也是这次报的 bug 要修的那个视图 —— 打开页面第一眼就该是对的。
 * 「按次」保留下来，是因为排查"某一轮为什么这么贵"时只有逐次明细说得清；
 * 两者是不同的问题，不是新旧替换关系。
 */
type UsageViewMode = 'conversation' | 'entry';
const viewMode = ref<UsageViewMode>('conversation');

/**
 * 按会话聚合一次要多少行。
 *
 * 契约上限就是 100，直接要满：聚合视图**没有分页**（`truncated` 只告诉你"还有更多"），
 * 要 20 行的话，一个月聊过 30 个话题的用户会在切换视图后以为记录丢了。
 * 真的超过 100 个会话时，页面会用 `describeUsageTruncation()` 明说"其余未显示"。
 */
const USAGE_LIMIT = 100;

const usage = ref<ConversationUsageResponse | null>(null);
const loadingUsage = ref(false);
const usageError = ref<string | null>(null);

async function loadUsageByConversation(): Promise<void> {
  loadingUsage.value = true;
  usageError.value = null;
  try {
    usage.value = await apiRequest<ConversationUsageResponse>(
      '/quota/ledger/conversations',
      ConversationUsageResponseSchema,
      { method: 'GET', query: { limit: USAGE_LIMIT } },
    );
  } catch (caught) {
    usageError.value = isApiError(caught) ? caught.message : '按会话统计加载失败，请稍后重试';
  } finally {
    loadingUsage.value = false;
  }
}

const usageRows = computed(() =>
  usage.value === null
    ? []
    : usageRowsOf(usage.value, conversationTitles.value, conversationIndexComplete.value),
);
const usageWindowText = computed(() => (usage.value === null ? '' : describeUsageWindow(usage.value)));
const usageItemCountText = computed(() =>
  usage.value === null ? '' : describeUsageItemCount(usage.value.itemCount),
);
const usageTotalsText = computed(() =>
  usage.value === null ? '' : describeUsageTotals(usage.value.rounds),
);
const usageTruncationText = computed(() =>
  usage.value === null ? null : describeUsageTruncation(usage.value.truncated, usage.value.items.length),
);
const usageEmptyText = computed(() => describeUsageEmpty(usageWindowText.value));

/** 切换按钮的样式：模板里塞三元表达式会让"当前选中"这件事散在两处 */
function tabClass(mode: UsageViewMode): string {
  return mode === viewMode.value
    ? 'rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-900'
    : 'rounded-md px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700';
}

/**
 * 首次加载三个数据源，**必须等会话恢复完成**。
 *
 * 冷启动（F5、直接打开 /quota、从书签进入）时这个组件会在 `runBootstrap()` 结束之前挂载，
 * 那一刻 `auth.hydrated` 还是 false。此前这里写成 `onMounted` 里判一次 `auth.hydrated`，
 * 结果挂载那一瞬间条件不成立，之后再也没有人来启动加载器：页面永远停在
 * 「暂无额度信息。/ 还没有消耗记录。」（不报错、也不重试），只有切走再切回来才会正常。
 * 改成 watch 就与"什么时候恢复完成"解耦了。
 */
let started = false;
watch(
  () => auth.hydrated && auth.status === 'authenticated',
  (ready) => {
    if (!ready || started) return;
    started = true;
    void loadQuota();
    void loadLedger();
    void loadUsageByConversation();
    void loadConversationIndex();
    // 套餐档次与到期时间：本页要在页脚显示它们，而 `/quota` 里只有 planId。
    // 单飞 + 未登录短路都在 store 里，这里重复调用是安全的。
    void loadSubscription();
  },
  { immediate: true },
);

const subscription = useSubscription();

/**
 * 每 60 秒推进一次的"现在"。
 *
 * 页脚的「剩余 N 天」必须自己走：否则把这个页面开着过夜，它第二天还在说"剩余 3 天"，
 * 而顶栏的警告已经出现了——同一份事实两处不一致，用户只会信错的那一处。
 */
const now = ref(Date.now());
let ticker: number | null = null;

onMounted(() => {
  ticker = window.setInterval(() => {
    now.value = Date.now();
  }, EXPIRY_TICK_MS);
});

onUnmounted(() => {
  if (ticker !== null) {
    window.clearInterval(ticker);
    ticker = null;
  }
});

const expiryStatus = computed(() => expiryStatusOf(subscription.subscription, now.value));
const cycleLabel = computed(() =>
  subscription.subscription === null
    ? null
    : describeBillingCycle(subscription.subscription.billingCycle),
);
const badgeTone = computed(() => planBadgeToneOf(quota.value?.planId ?? 'free', expiryStatus.value));

/** 「到期时间：2026-11-05 23:59（剩余 30 天）」。没有订阅时明确说"无订阅"而不是留空。 */
const expiryText = computed(() => {
  const current = subscription.subscription;
  if (current === null) {
    return '到期时间：—（当前档位无订阅）';
  }
  const status = expiryStatus.value;
  if (status.kind === 'none') {
    return `到期时间：${formatDateTime(current.expiresAt)}`;
  }
  return `到期时间：${formatDateTime(current.expiresAt)}（${describeRemaining(status)}）`;
});

const showRenewLink = computed(
  () => expiryStatus.value.kind === 'expiring' || expiryStatus.value.kind === 'expired',
);

const needsLogin = computed(() => auth.hydrated && auth.status !== 'authenticated');

/**
 * 被拦的是哪一维（都没被拦则为 null）。
 *
 * 日额度与月额度的恢复时间差了一个量级，必须分开报：说"今日额度已用完"而实际是
 * 月度封顶，用户会等一整天再回来，而这一整天里他其实什么都做不了——
 * 该做的是升级套餐。
 *
 * 判据就是 `remainingPercent`：服务端给的是**向下取整**后的整数，所以 0 可能
 * 代表"真的耗尽"也可能是"还剩不到 1%"——两种情况下"该升档了"的结论都一样，
 * 而任何"反推具体余量"的尝试都会重新引入一个前端自己算的数字。
 * `unlimited` 的账号永远不算被拦。
 */
const quotaBlocked = computed<'daily' | 'monthly' | null>(() => {
  const current = quota.value;
  if (current === null || current.unlimited || current.remainingPercent > 0) {
    return null;
  }
  return current.scope;
});
</script>

<template>
  <div v-if="needsLogin" class="mx-auto w-full max-w-2xl px-4 py-12 text-center">
    <h1 class="text-lg font-semibold text-slate-900">登录后查看额度</h1>
    <p class="mt-2 text-sm text-slate-600">额度和消耗明细都记在你的账号下。</p>
    <RouterLink to="/login" class="mt-6 inline-block"><Button>去登录</Button></RouterLink>
  </div>

  <div v-else class="mx-auto w-full max-w-2xl px-4 py-6">
    <h1 class="text-xl font-semibold text-slate-900">额度与消耗</h1>
    <p class="mt-1.5 text-sm leading-relaxed text-slate-600">
      每次回答都会占用你的额度（提问 + 回答）。上游故障、超时或安全拦截的回答<strong
        class="font-medium text-slate-700"
        >不计入消耗</strong
      >。剩余额度以百分比展示，具体用量由服务端统计。
    </p>

    <p
      v-if="error !== null"
      role="alert"
      class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      {{ error }}
    </p>

    <section class="mt-5 rounded-xl border border-slate-200 bg-white p-4">
      <SkeletonText v-if="quota === null && loadingQuota" :lines="3" />
      <p v-else-if="quota === null" class="text-sm text-slate-500">暂无额度信息。</p>

      <div v-else class="space-y-4">
        <!--
          一个百分比，而不是日/月两根：服务端判定的是"最先撞上的那一维"，
          两处各画一根必然出现"进度条说还剩 15%、明细说还剩 3%"。
          `scope` 与 `resetAt` 成对渲染，用户才知道这个数字属于哪一维、何时恢复。
        -->
        <UsageMeter
          :unlimited="quota.unlimited"
          :scope="quota.scope"
          :remaining-percent="quota.remainingPercent"
          :reset-at="quota.resetAt"
          :now="now"
        />

        <div class="space-y-1.5 border-t border-slate-100 pt-3 text-xs text-slate-500">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="flex flex-wrap items-center gap-2">
              套餐：{{ planLabelOf(quota.planId) }}
              <span class="text-slate-400">
                （{{ quota.planId }}{{ cycleLabel === null ? '' : ` · ${cycleLabel}` }}）
              </span>
              <PlanBadge
                :plan-id="quota.planId"
                :tone="badgeTone"
                :status="expiryStatus"
              />
            </span>
            <span>
              {{ quotaScopeTitleOf(quota.scope) }}下次重置：{{ formatDateTime(quota.resetAt) }}
            </span>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-2">
            <span :class="expiryStatus.kind === 'expired' ? 'font-medium text-amber-700' : ''">
              {{ expiryText }}
            </span>
            <RouterLink
              v-if="showRenewLink"
              to="/checkout"
              class="font-medium text-brand-700 hover:underline"
            >
              立即续费
            </RouterLink>
          </div>
        </div>

        <div
          v-if="quotaBlocked !== null"
          class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-relaxed text-red-900"
        >
          <p>
            {{ quotaBlocked === 'monthly' ? '本月额度已用完' : '今日额度已用完' }}，将在
            {{ formatDateTime(quota.resetAt) }} 重置。需要立刻继续使用，可升级套餐。
          </p>
          <RouterLink to="/checkout" class="mt-2 inline-block">
            <Button size="sm">升级套餐</Button>
          </RouterLink>
        </div>
      </div>
    </section>

    <section class="mt-6">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h2 class="text-base font-semibold text-slate-900">消耗明细</h2>
        <div
          class="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5"
          role="tablist"
          aria-label="消耗明细视图"
        >
          <button
            type="button"
            role="tab"
            :aria-selected="viewMode === 'conversation'"
            :class="tabClass('conversation')"
            @click="viewMode = 'conversation'"
          >
            按会话
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="viewMode === 'entry'"
            :class="tabClass('entry')"
            @click="viewMode = 'entry'"
          >
            按次
          </button>
        </div>
      </div>

      <p v-if="viewMode === 'conversation'" class="mt-1 text-xs text-slate-500">
        每个会话一行，显示它占本期消耗的比例。点会话标题可以回到那次对话。
      </p>
      <p v-else class="mt-1 text-xs text-slate-500">
        按时间倒序，每行是一次回答的结算记录。点会话标题可以回到那次对话。
      </p>

      <template v-if="viewMode === 'conversation'">
        <p
          v-if="usageError !== null"
          role="alert"
          class="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          {{ usageError }}
        </p>
        <SkeletonText v-else-if="usage === null && loadingUsage" :lines="4" class="mt-3" />
        <p v-else-if="usageRows.length === 0" class="mt-3 text-sm text-slate-500">
          {{ usageEmptyText }}
        </p>

        <template v-else>
          <div class="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>{{ usageWindowText }}</span>
            <span class="tabular-nums">{{ usageItemCountText }} · {{ usageTotalsText }}</span>
          </div>

          <ul
            class="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white"
          >
            <li
              v-for="row in usageRows"
              :key="row.key"
              class="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <div class="min-w-0">
                <RouterLink
                  v-if="row.owner.href !== null"
                  :to="row.owner.href"
                  class="text-sm text-brand-700 hover:underline"
                  :title="row.owner.hint ?? undefined"
                >
                  {{ row.owner.label }}
                </RouterLink>
                <span v-else class="text-sm text-slate-500" :title="row.owner.hint ?? undefined">
                  {{ row.owner.label }}
                </span>
                <p class="mt-0.5 text-xs text-slate-400">
                  {{ describeUsageRounds(row.rounds) }} · {{ describeUsageLastAt(row.lastAt) }}
                </p>
              </div>
              <span class="shrink-0 text-sm tabular-nums text-slate-700">
                {{ describeUsageShare(row.sharePercent) }}
              </span>
            </li>
          </ul>

          <p v-if="usageTruncationText !== null" class="mt-2 text-xs text-amber-700">
            {{ usageTruncationText }}
          </p>
        </template>
      </template>

      <template v-else>
        <SkeletonText v-if="entries.length === 0 && loadingLedger" :lines="4" class="mt-3" />
        <p v-else-if="entries.length === 0" class="mt-3 text-sm text-slate-500">还没有消耗记录。</p>

        <ul v-else class="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          <li
            v-for="row in rows"
            :key="row.entry.id"
            class="flex items-center justify-between gap-3 px-4 py-2.5"
          >
            <div class="min-w-0">
              <RouterLink
                v-if="row.owner.href !== null"
                :to="row.owner.href"
                class="text-sm text-brand-700 hover:underline"
                :title="row.owner.hint ?? undefined"
              >
                {{ row.owner.label }}
              </RouterLink>
              <span v-else class="text-sm text-slate-500" :title="row.owner.hint ?? undefined">
                {{ row.owner.label }}
              </span>
              <p class="mt-0.5 text-xs text-slate-400">{{ formatDateTime(row.entry.createdAt) }}</p>
            </div>
            <!--
              服务端只回结算行（`kind === 'commit'`），返还/预扣不会有行进来；
              但"是返还还是扣减"必须由 `kind` 判定 —— 旧实现靠金额正负来猜，
              而金额已经不在契约里了。
            -->
            <span
              class="shrink-0 text-sm"
              :class="row.entry.kind === 'release' ? 'text-green-600' : 'text-slate-700'"
            >
              {{ describeLedgerKind(row.entry.kind) }}
            </span>
          </li>
        </ul>

        <div v-if="hasMore && entries.length > 0" class="mt-3 text-center">
          <Button variant="secondary" size="sm" :disabled="loadingLedger" @click="() => void loadLedger()">
            {{ loadingLedger ? '加载中…' : '加载更多' }}
          </Button>
        </div>
        <p v-else-if="!hasMore && entries.length > 0" class="mt-3 text-center text-xs text-slate-400">
          没有更多记录了
        </p>
      </template>
    </section>
  </div>
</template>
